import {
  decorate,
  observable,
  reaction,
  action,
  configure,
  runInAction,
} from 'mobx';
import history from '../browserHistory';
import {
  setAuthErrorValue,
  getStoredAuthError,
} from '../shared/utils/authToken';
import SignalRMiddleware from '../shared/utils/SignalRMiddleware';
import { getWsHost } from '../endpoints';
import { toTitleCase, getSessionToken } from './utils';
import { messages } from './../shared/constants/messages';
import {
  sessionTokenKey,
  connectionClosedAbnormallyCode,
  socketErrorCodes,
} from '../shared/constants';

import {
  getRouteFromPathName,
  getStoredValue,
  clearStorage,
  removeStoredValue,
  removeStoredSessionValue,
  setStoredValue,
  getStoredSessionValue,
  setStoredSessionValue,
} from '../shared/utils/localStorage';

configure({ enforceActions: 'always' });

const getWindowReloads = () =>
  Number(getStoredSessionValue('windowReloads') || 0);
const bumpWindowReloadCount = () => {
  const reloads = getWindowReloads();
  setStoredSessionValue('windowReloads', reloads + 1);
};
const removeWindowReloads = () => removeStoredSessionValue('windowReloads');

const forceLogoutIfGoesHaywire = () => {
  if (getWindowReloads() > 1) {
    removeWindowReloads();
    removeStoredSessionValue(sessionTokenKey);
    window.location.reload();
  }
};
export default class APIWebSocketStore {
  constructor(EndpointStore, AuthStore, DashboardStore) {
    runInAction('APIWebSocketStore', () => {
      this.dashboardStore = DashboardStore;
      this.authStore = AuthStore;
      this.endpointStore = EndpointStore;
      this.connection = null;
      this.dashboardStore.connected = false;
      this.error = null;
      this.reconnecting = false;
      this.softLoadingVisible = false;

      if (!getSessionToken()) {
        history.push('/login');
      }
      this.handleReconnect();
    });

    reaction(
      () => this.dashboardStore.connected,
      (state) => {
        this.dashboardStore.setProperty('connected', state);
        this.dashboardStore.initialize();
      },
    );

    reaction(
      () => this.error,
      (error) => {
        if (
          socketErrorCodes.filter((code) => error.message.indexOf(code) > -1)
            .length > 0
        ) {
          this.authStore.setProperty(
            'errorMessage',
            messages.invalidUsernamePassword,
          );
        }
        this.toggleSoftLoading(false);
      },
    );
  }

  apiconnect = (username, password, providerId, sessionToken = null) => {
    this.toggleSoftLoading(true);
    try {
      if (!username) {
        setAuthErrorValue('authError', messages.userNameRequired);
        throw 'authError';
      } else if (!password) {
        setAuthErrorValue('authError', messages.passwordRequired);
        throw 'authError';
      } else if (!providerId) {
        setAuthErrorValue('authError', messages.providerRequired);
        throw 'authError';
      }
      const version = window.localStorage.getItem('cacheVersion');
      this.endpointStore.Auth.login(username, password, providerId, version)
        .then((result) => {
          this.loggedInMember = result;
          if (this.loggedInMember && this.loggedInMember.sessionToken) {
            sessionToken = this.loggedInMember.sessionToken;
            setStoredSessionValue(sessionTokenKey, sessionToken);

            runInAction('apiconnect', () => {
              this.authStore.setProperty(
                'memberName',
                this.loggedInMember.member.member,
              );
              this.authStore.setProperty('connected', true);
              this.dashboardStore.setProperty('connected', true);
              this.toggleSoftLoading(false);
            });
            this.connect(null, null, sessionToken);
            history.push('/dashboard');
          }
        })
        .catch((error) => {
          let authError = getStoredAuthError('authError');
          if (error === 'authError' && authError) {
            this.authStore.setProperty('errorMessage', authError);
          } else {
            console.error(messages.apiRequestFailed, error);
            this.authStore.setProperty(
              'errorMessage',
              messages.invalidUsernamePassword,
            );
          }
          runInAction('apiconnect error', () => {
            this.error = error;
            this.dashboardStore.setProperty('connected', false);
            this.reconnecting = false;
            clearStorage();
          });
          this.disconnect();
          this.toggleSoftLoading(false);
        });
    } catch (error) {
      let authError = getStoredAuthError('authError');
      if (error === 'authError' && authError) {
        this.authStore.setProperty('errorMessage', authError);
      } else {
        console.error(messages.apiRequestFailed, error);
        this.authStore.setProperty(
          'errorMessage',
          messages.invalidUsernamePassword,
        );
      }
      runInAction('apiconnect error', () => {
        this.error = error;
        this.dashboardStore.setProperty('connected', false);
        this.reconnecting = false;
        clearStorage();
      });
      this.disconnect();
      this.toggleSoftLoading(false);
    }
  };

  toggleSoftLoading = (value) => {
    this.softLoadingVisible = value;

    const timeout = setTimeout(() => {
      runInAction('toggleLoading', () => {
        this.softLoadingVisible = false;
        clearTimeout(timeout);
      });
    }, 1000);
  };

  connect = (username, password, sessionToken) => {
    // don't establish a connection to socket if we are on the dashboard already
    if (!['/', '/dashboard'].includes(history.location.pathname)) return;

    this.toggleSoftLoading(true);
    this.connection = this.OpenSocket(username, password, sessionToken);
    this.connection
      .start()
      .then(async () => {
        bumpWindowReloadCount();

        console.log(messages.connectionStarted);
        runInAction('connect', () => {
          this.dashboardStore.setProperty('connected', true);
          this.listeners();
        });
        this.toggleSoftLoading(false);
      })
      .catch((error) => {
        console.error(messages.connectionStartFailed, error);
        runInAction(() => {
          this.error = error;
        });

        if (error.message.includes('1006')) {
          this.authStore.setProperty(
            'errorMessage',
            messages.invalidUsernamePassword,
          );
          this.disconnect();
        }
        runInAction('connect error', () => {
          this.error = error;
          this.dashboardStore.setProperty('connected', false);
          this.reconnecting = false;
          clearStorage();
        });
        this.toggleSoftLoading(false);
      });

    this.connection.onclose(() => {
      console.log(messages.connectionClosing);
      this.dashboardStore.setProperty('connected', false);
    });

    this.connection.onreconnecting((error) => {
      // TODO: We're not getting a specific error code back for invalid username/password. This needs to be added server side.
      console.log(messages.connectionReconnecting);
      if (error.message.includes(connectionClosedAbnormallyCode)) {
        this.authStore.setProperty(
          'errorMessage',
          messages.invalidUsernamePassword,
        );
        this.disconnect();
      }
    });

    this.connection.onreconnected(async () => {
      console.log(messages.connectionReconnected, this.connection.state);
      this.webSocketStore.sendMessage({
        method: 'ChangeLane',
        payload: [this.dashboardStore.getAuctionLaneIdForInvocation],
      });
    });
  };

  OpenSocket = (username, password, sessionToken = null) => {
    let route = toTitleCase(getRouteFromPathName());

    if (route === 'Login') {
      route = 'DashboardReport';
    }

    let url = `?Name=${route}&Username=${username}&Password=${password}&MemberType=ProviderAdmin&MemberRole=${route}`;
    if (sessionToken) {
      url = url + `&SessionToken=${sessionToken}`;
    }
    const host = getWsHost();
    const socket = new SignalRMiddleware(host, url).openSocket();
    console.log(messages.connectionEstablished);
    return socket;
  };

  handleReconnect = () => {
    forceLogoutIfGoesHaywire();

    const sessionToken = getSessionToken();
    if (sessionToken) {
      this.reconnecting = true;
      this.connect(null, null, sessionToken);

      /** Protects against getting caught in a loading spinner loop */
      const timeout = setTimeout(() => {
        runInAction('handleReconnect', () => {
          this.reconnecting = false;
          clearTimeout(timeout);
        });
      }, 5000);
    }
  };

  sendMessage = async ({ method = null, payload = [] }) => {
    if (!method) throw new Error(messages.methodRequired);

    let _payload = [...payload];
    _payload.unshift(method);
    this.connection.invoke(..._payload).catch((error) => {
      console.error(messages.invocationFailed, { method, error });
      runInAction(() => {
        this.error = error;
      });
    });
  };

  disconnect = () => {
    console.log(
      this.connection ? messages.disconnectAttempt : messages.disconnectAborted,
    );
    if (!this.connection) return;
    this.connection
      .stop()
      .then(() => {
        runInAction('disconnect', () => {
          this.reconnecting = false;
          this.dashboardStore.setProperty('connected', false);
          this.dashboardStore.setProperty('onDashboardScreen', false);
        });
        console.log(messages.disconnectSucceeded);
      })
      .catch((error) => {
        runInAction('disconnect error', () => {
          this.error = error;
          this.dashboardStore.connected = false;
        });
        console.error(messages.disconnectFailed, error);
      });
  };

  listeners = () => {
    const events = [
      'AuctionInfo',
      'ConnectionInfo',
      'DashboardReport',
      'Disconnect',
      'LaneMetrics',
      'LayoutSettings',
      'Product',
      'SessionSettings',
      'SystemMessage',
    ];
    events.map((name) => {
      this.connection.on(name, (...payload) =>
        this._emit({ method: name, payload }),
      );
    });
  };

  _emit = async (object, ...rest) => {
    switch (object.method) {
      case 'AuctionInfo': {
        console.log('🧑‍⚖️ - auction', object.payload);
        this.auctionStore.setAuctionInfo(object.payload[1]);
        break;
      }
      case 'ConnectionInfo': {
        console.log(messages.engineMessageReceived, '> ConnectionInfo');
        removeWindowReloads();
        const { name } = object.payload[0];
        this.authStore.setProperty('memberName', name);
        this.authStore.setProperty('connectionInfo', object.payload[0]);
      }
      case 'DashboardReport': {
        let auctionMetrics = object.payload[0];
        // console.log('dashboardreport', JSON.stringify(auctionMetrics));
        this.dashboardStore.setDashboardMetrics(auctionMetrics);
        this.dashboardStore.setProperty('emitTimestamp', new Date());
        break;
      }
      case 'Disconnected': {
        console.log(messages.engineMessageReceived, '> Disconnected');
        this.authStore.setProperty('connectionInfo', null);
        break;
      }
      case 'LaneMetrics': {
        console.log(
          messages.engineMessageReceived,
          '> 📈 LaneMetrics',
          object.payload[1],
        );
        this.auctionStore.setLaneMetrics(object.payload[1]);
        break;
      }
      case 'LayoutSettings': {
        this.auctionStore.setLayoutSettings(object.payload[0]);
        break;
      }
      case 'Product': {
        console.log(
          messages.engineMessageReceived,
          '> Product',
          object.payload[1],
        );
        this.bidStore.setProduct(object.payload[1]);
        break;
      }
      case 'SessionSettings': {
        this.authStore.setSettings(object.payload[0]);
        break;
      }
      case 'SystemMessage': {
        console.log(
          messages.engineMessageReceived,
          '> SystemMessage',
          object.payload,
        );
        break;
      }
      default: {
        console.error(`Unhandled WebSocket message received`, object);
        break;
      }
    }
  };
}

decorate(APIWebSocketStore, {
  connect: action,
  connected: observable,
  connection: observable,
  disconnect: action,
  handleReconnect: action,
  handleRejoin: action,
  reconnecting: observable,
  sendMessage: action,
  softLoadingVisible: observable,
  toggleSoftLoading: action,
  error: observable,
});
